import { useMutation, useSubscription } from '@apollo/client';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { logEvent } from 'services/logger';

import {
  NOTIFICATION_PUBLISHED_SUBSCRIPTION,
  NotificationPublishedSubscriptionResponse,
  NotificationPublishedSubscriptionVariables,
  READ_NOTIFICATION,
  ReadNotificationInput,
} from './graphql';
import {
  InAppStackActionType,
  StoredSubscriptionNotification,
  inAppStackInitialState,
  inAppStackReducer,
} from './inAppNotificationStackReducer';

const STACK_DEPTH = 3;
const AUTO_DISMISS_MS = 10_000;

/** Row passed to `InAppNotificationStack` (subscription only). */
export interface SubscriptionStackItem {
  id: string;
  createdAt: number;
  notificationId: string;
  channelId: string;
  subject: string;
  messageHtml: string;
  url?: string | null;
}

export interface UseInAppNotificationStackConfig {
  accountId: string;
}

function toVisibleItems(items: StoredSubscriptionNotification[]): SubscriptionStackItem[] {
  return [...items]
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, STACK_DEPTH)
    .map((s) => ({
      id: s.id,
      createdAt: s.createdAt,
      notificationId: s.notificationId,
      channelId: s.channelId,
      subject: s.subject,
      messageHtml: s.messageHtml,
      url: s.url,
    }));
}

export const useInAppNotificationStack = ({
  accountId,
}: UseInAppNotificationStackConfig): {
  visibleItems: SubscriptionStackItem[];
  dismissItem: (item: SubscriptionStackItem) => void;
  navigateTo: (url: string) => void;
  pathname: string;
} => {
  const { push } = useHistory();
  const { pathname } = useLocation();
  const [{ subscriptionItems }, dispatch] = useReducer(inAppStackReducer, inAppStackInitialState);

  const [readNotification] = useMutation<unknown, ReadNotificationInput>(READ_NOTIFICATION, {
    onError: (err) =>
      logEvent('[useInAppNotificationStack] Read notification failed', { accountId, error: err }),
  });

  const pushSubscription = useCallback(
    (payload: Omit<StoredSubscriptionNotification, 'id' | 'createdAt'>) => {
      const createdAt = Date.now();
      const item: StoredSubscriptionNotification = {
        ...payload,
        id: `${payload.notificationId}-${createdAt}`,
        createdAt,
      };
      dispatch({ type: InAppStackActionType.PushSubscription, payload: item });
    },
    []
  );

  const dismissItem = useCallback(
    (item: SubscriptionStackItem) => {
      dispatch({ type: InAppStackActionType.Remove, payload: { id: item.id } });
      void readNotification({
        variables: {
          channelId: item.channelId,
          notificationId: item.notificationId,
          accountId,
        },
      });
    },
    [accountId, readNotification]
  );

  const navigateTo = useCallback((url: string) => push(url), [push]);

  useSubscription<
    NotificationPublishedSubscriptionResponse,
    NotificationPublishedSubscriptionVariables
  >(NOTIFICATION_PUBLISHED_SUBSCRIPTION, {
    skip: !accountId,
    variables: { accountId },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      if (!data) return;
      const {
        notificationPublished: { subject, message, url, channel, id: notificationId },
      } = data;
      pushSubscription({
        notificationId,
        channelId: channel.id,
        subject,
        messageHtml: message,
        url,
      });
    },
  });

  const visibleItems = useMemo(() => toVisibleItems(subscriptionItems), [subscriptionItems]);

  const top = visibleItems[0];

  useEffect(() => {
    if (!top) return;
    const captured = top;
    const timer = window.setTimeout(() => {
      dismissItem(captured);
    }, AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [top, dismissItem]);

  return {
    visibleItems,
    dismissItem,
    navigateTo,
    pathname,
  };
};
